import {
  Security as Shield,
  View as Eye,
  Locked as Lock,
  Share as Share2,
  Time as Clock,
  UserCertification as UserCheck,
  Cookie,
  Notification as Bell,
  Email as Mail,
} from '@carbon/icons-react'

const sections = [
  {
    id: 'interpretation',
    icon: Shield,
    title: 'Interpretation and Definitions',
    plain:
      'Explains key terms used throughout this policy so everyone understands the same meaning.',
    content: (
      <>
        <p>
          The words with capital first letters have defined meanings. These
          meanings apply whether used in singular or plural form.
        </p>
        <p className="font-medium mt-4 text-foreground">Definitions</p>
        <ul className="mt-2 space-y-2 text-sm text-muted-foreground leading-relaxed">
          <li>
            <strong>Account</strong>: unique account created to access our
            Service.
          </li>
          <li>
            <strong>Affiliate</strong>: entity that controls, is controlled by,
            or is under common control with us (≥50% ownership).
          </li>
          <li>
            <strong>Company</strong> ("We", "Us", "Our"): Euclideum Solutions
            Pvt. Ltd., 203A, Shiv Heights, Ambedkar Chowk, Thane West – 400601.
          </li>
          <li>
            <strong>Cookies</strong>: small files placed on your device
            containing browsing history details.
          </li>
          <li>
            <strong>Country</strong>: Maharashtra, India.
          </li>
          <li>
            <strong>Personal Data / Personal Information</strong>: any
            information relating to an identified or identifiable individual.
          </li>
          <li>
            <strong>Service</strong>: the website https://euclideum.com/.
          </li>
          <li>
            <strong>Service Provider</strong>: third-party companies/individuals
            processing data on our behalf.
          </li>
          <li>
            <strong>Usage Data</strong>: automatically collected data from use
            of or Service infrastructure.
          </li>
          <li>
            <strong>You</strong>: the individual or legal entity accessing/using
            the Service.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'collection',
    icon: Eye,
    title: 'Collecting and Using Your Personal Data',
    plain:
      'We collect contact details you provide, automatic usage/technical data, and use cookies to improve the experience.',
    content: (
      <>
        <p className="font-medium text-foreground">Types of Data Collected</p>
        <p className="mt-2 font-medium">Personal Data</p>
        <ul className="mt-1.5 space-y-1.5 text-sm text-muted-foreground list-disc pl-5">
          <li>Email address</li>
          <li>First name and last name</li>
          <li>Phone number</li>
          <li>Address, State, Province, ZIP/Postal code, City</li>
        </ul>

        <p className="mt-5 font-medium">Usage Data</p>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Collected automatically: IP address, browser type/version, pages
          visited, time/date, duration, device identifiers, mobile device info,
          etc.
        </p>

        <p className="mt-5 font-medium">Tracking Technologies and Cookies</p>
        <p className="mt-1.5 text-sm text-muted-foreground">
          We use Cookies, Web Beacons, tags, and scripts. You can refuse cookies
          via browser settings, but some features may not work.
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          <strong>Essential Cookies</strong>: required for core functionality
          (session, authentication).
          <br />
          <strong>Preference Cookies</strong>: remember choices (language, login
          info).
          <br />
          Non-essential cookies used only with consent (you can withdraw consent
          anytime).
        </p>
      </>
    ),
  },
  {
    id: 'use',
    icon: UserCheck,
    title: 'Use of Your Personal Data',
    plain:
      'We use your data to deliver the service, manage your account, send updates, fulfill contracts, provide support, improve the product, and meet legal obligations.',
    content: (
      <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-5">
        <li>Provide and maintain the Service</li>
        <li>Manage your Account & registration</li>
        <li>Perform contracts (purchases, services)</li>
        <li>Contact you (updates, security notices, support)</li>
        <li>
          Send news/offers about similar products/services (opt-out available)
        </li>
        <li>Handle your requests</li>
        <li>Business transfers (merger, acquisition, etc.)</li>
        <li>Data analysis, trend identification, service improvement</li>
      </ul>
    ),
  },
  {
    id: 'sharing',
    icon: Share2,
    title: 'Sharing & Disclosure of Your Personal Data',
    plain:
      'We share data only with service providers helping us run the business, during company sales/transfers, with affiliates, or when legally required.',
    content: (
      <>
        <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-5">
          <li>With Service Providers (analytics, support, hosting…)</li>
          <li>For business transfers (merger, asset sale…)</li>
          <li>With Affiliates (companies under common control)</li>
          <li>With business partners (for certain offers only with consent)</li>
          <li>In public areas of the Service (if you post publicly)</li>
          <li>With your consent</li>
          <li>
            To comply with law, protect rights/safety, respond to public
            authority requests
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'retention',
    icon: Clock,
    title: 'Retention of Your Personal Data',
    plain:
      'We keep data only as long as needed for the purposes described or required by law typically up to 24 months for most categories after the relationship ends.',
    content: (
      <>
        <p>
          We retain data to fulfill the purposes of this policy and comply with
          legal obligations.
        </p>
        <p className="mt-3 font-medium">Typical retention periods:</p>
        <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground list-disc pl-5">
          <li>
            Account information: duration of account + up to 24 months after
            closure
          </li>
          <li>
            Support tickets & chat transcripts: up to 24 months after closure
          </li>
          <li>Website analytics / Usage Data / Server logs: up to 24 months</li>
          <li>
            Longer when required by law, for legal claims, backups, or your
            explicit request
          </li>
        </ul>
        <p className="mt-4 text-sm text-muted-foreground">
          After retention period ends: secure deletion, anonymization, or (for
          backups) eventual scheduled removal.
        </p>
      </>
    ),
  },
  {
    id: 'transfer',
    icon: Share2,
    title: 'Transfer of Your Personal Data',
    plain:
      'Your data may be processed or stored outside India. We use appropriate safeguards for international transfers as required by law.',
    content: (
      <p className="text-sm text-muted-foreground leading-relaxed">
        Data is processed in India and possibly in other countries where our
        service providers or affiliates are located. We ensure appropriate
        safeguards are in place for cross-border transfers in line with
        applicable data protection laws.
      </p>
    ),
  },
  {
    id: 'your-rights',
    icon: UserCheck,
    title: 'Your Rights Access, Correction & Deletion',
    plain:
      'You can request access, correction, or deletion of your data. You can also update/delete some information directly in your account.',
    content: (
      <>
        <p className="text-sm text-muted-foreground">
          You may update, correct or delete your information via your Account
          settings (if applicable) or by contacting us.
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          We may retain certain data when required by law or for legitimate
          business/legal reasons.
        </p>
      </>
    ),
  },
  {
    id: 'cookies',
    icon: Cookie,
    title: 'Cookies & Tracking Technologies',
    plain:
      'We use essential cookies to make the site work and analytics cookies to understand usage (with consent where required).',
    content: (
      <p className="text-sm text-muted-foreground">
        See the <strong>Cookies</strong> section earlier in this policy for
        detailed information about types of cookies, purposes, and how to manage
        them.
      </p>
    ),
  },
  {
    id: 'security',
    icon: Lock,
    title: 'Security of Your Personal Data',
    plain:
      'We use commercially reasonable security measures, but no method is 100% secure.',
    content: (
      <p className="text-sm text-muted-foreground">
        While we strive to protect your Personal Data, we cannot guarantee
        absolute security of transmission or storage over the Internet.
      </p>
    ),
  },
  {
    id: 'children',
    icon: Shield,
    title: "Children's Privacy",
    plain:
      'Our Service is not directed at anyone under 16. We do not knowingly collect data from children under 16.',
    content: (
      <p className="text-sm text-muted-foreground">
        If you are a parent/guardian and believe your child has provided us with
        Personal Data, please contact us immediately. We will remove such
        information if we become aware of it without verified parental consent.
      </p>
    ),
  },
  {
    id: 'changes',
    icon: Bell,
    title: 'Changes to this Privacy Policy',
    plain:
      'We may update this policy. You will be notified of material changes via email or prominent notice on the website.',
    content: (
      <>
        <p className="text-sm text-muted-foreground">
          We will post the new policy here and update the "Last updated" date.
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          You are advised to review this page periodically. Changes are
          effective when posted.
        </p>
      </>
    ),
  },
  {
    id: 'contact',
    icon: Mail,
    title: 'Contact Us',
    plain:
      'Reach out if you have any questions about this policy or your data.',
    content: (
      <div className="rounded-xl bg-muted border border-border p-6 space-y-3 text-sm">
        <div className="flex gap-4">
          <span className="w-24 font-medium text-foreground">Email:</span>
          <a
            href="mailto:hq@euclideum.com"
            className="text-emerald-600 dark:text-primary-active hover:underline"
          >
            hq@euclideum.com
          </a>
        </div>
        <div className="flex gap-4">
          <span className="w-24 font-medium text-foreground">Website:</span>
          <a
            href="https://euclideum.com/"
            className="text-emerald-600 dark:text-primary-active hover:underline"
          >
            https://euclideum.com/
          </a>
        </div>
        <div className="flex gap-4">
          <span className="w-24 font-medium text-foreground">Company:</span>
          <span>Euclideum Solutions Pvt. Ltd.</span>
        </div>
      </div>
    ),
  },
]

export function PrivacySections() {
  return (
    <>
      {sections.map(({ id, icon: Icon, title, plain, content }) => (
        <section key={id} id={id} className="scroll-mt-24">
          <div className="flex items-start gap-4 mb-5">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600/10 dark:bg-primary-active/10 border border-emerald-600/20 dark:border-primary-active/20 mt-0.5">
              <Icon className="size-5.5 text-emerald-600 dark:text-primary-active" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-semibold text-foreground">
                {title}
              </h2>
              <div className="mt-3 rounded-lg border border-emerald-600/20 dark:border-primary-active/20 bg-emerald-600/5 dark:bg-primary-active/5 px-4 py-3">
                <p className="text-sm text-foreground leading-relaxed">
                  <span className="font-semibold">In plain English: </span>
                  {plain}
                </p>
              </div>
            </div>
          </div>

          <div className="ml-14 space-y-4 text-sm text-muted-foreground leading-relaxed">
            {content}
          </div>

          <div className="ml-14 mt-10 border-b border-border pb-1" />
        </section>
      ))}
    </>
  )
}
